// /profile/khusbu-thapa.jsx
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";

// ---- Quick content knobs ----
import avatar from "./images/khusbu-profile.jpeg";
const LOCATION = "Tysons Corner / Reston, Virginia, USA";
const TAGLINE = "Agile Developer II • Full-Stack Java/Angular • Cloud & DevOps";

const LINKS = {
  linkedin: "https://www.linkedin.com/in/khusbu-thapa/",
  // Add a resume PDF later if you have one:
  resume: null, // e.g. "/assets/khusbu-thapa-resume.pdf"
  github: null, // e.g. "https://github.com/<username>"
};

// Keep email/phone as placeholders for now (safe to publish later)
const EMAIL = "khusbu.thapa@example.com"; // placeholder
const PHONE = "(555) 555-5555"; // placeholder

const ABOUT =
  "Agile developer with hands-on experience across Java/Spring Boot, AngularJS, REST APIs, and secure, cloud-ready backends. Passionate about building reliable financial services software with clean architecture, CI/CD, strong observability, and an ownership mindset.";

const SKILL_GROUPS = [
  {
    label: "Languages & Frameworks",
    items: [
      "Java 11 / 17",
      "Spring Boot",
      "Spring Framework",
      "Jakarta JAXB",
      "AngularJS",
      "JavaScript",
      "JSON / XML",
    ],
  },
  {
    label: "Cloud & DevOps",
    items: [
      "AWS (S3, SNS)",
      "Azure Key Vault",
      "HashiCorp Vault",
      "Jenkins",
      "SonarQube",
      "Bitbucket",
      "Autosys",
      "Swagger / OpenAPI",
    ],
  },
  {
    label: "Messaging & Security",
    items: [
      "ActiveMQ / Message Queue",
      "JWT",
      "PingFederate",
      "Active Directory",
      "RSA Tokens",
    ],
  },
  {
    label: "Databases & Search",
    items: ["PostgreSQL", "Relational DBs", "Elasticsearch", "Kibana"],
  },
  {
    label: "Process & Tools",
    items: ["Agile / Scrum", "Jira", "Confluence", "Production Support", "Debugging"],
  },
];

const EXPERIENCE = [
  {
    company: "Freddie Mac",
    location: "Tysons Corner, VA, USA",
    period: "Mar 2025 – Present",
    role: "Agile Developer II (On-site)",
    bullets: [
      "Contributing to full-stack features across AngularJS front end and Spring Boot microservices.",
      "Applying Agile practices, CI/CD, automated quality gates, and secure configuration via Vault/Key Vault.",
      "Hands-on with production support, debugging, and performance improvements in a regulated domain.",
    ],
  },
  {
    company: "Freddie Mac",
    location: "Tysons Corner, VA, USA (Hybrid)",
    period: "Feb 2024 – Mar 2025",
    role: "Agile Developer",
    bullets: [
      "Built and maintained REST APIs using Spring Boot; integrated with PostgreSQL and ActiveMQ.",
      "Employed AWS services (S3, SNS) and secrets management (HashiCorp Vault, Azure Key Vault).",
      "Implemented CI/CD with Jenkins, code quality with SonarQube; used Swagger/OpenAPI for service docs.",
      "Worked with PingFederate/AD for authentication, JWT authorization, and secure app configuration.",
      "Tooling: Bitbucket, Jira, Confluence; observability with Kibana/Elasticsearch.",
    ],
  },
  {
    company: "Hexaware Technologies",
    location: "Reston, VA, USA (Hybrid)",
    period: "Sep 2023 – Feb 2024",
    role: "Full Stack Engineer",
    bullets: [
      "Delivered features across Spring Boot services and PostgreSQL data layer.",
      "Participated in Agile ceremonies; contributed to code reviews and deployment pipelines.",
    ],
  },
  {
    company: "Revature",
    location: "Virginia, USA (Remote)",
    period: "Aug 2023 – Sep 2023",
    role: "Software Engineer",
    bullets: [
      "Trained on Spring Boot & modern Java; practiced team workflows and CI/CD fundamentals.",
    ],
  },
  {
    company: "ISHANI TECHNOLOGY PVT LTD",
    location: "Lalitpur, Nepal (On-site)",
    period: "Jan 2021 – Dec 2022",
    role: "Junior Java Developer",
    bullets: [
      "Developed REST APIs and web apps with Core Java, JEE, Spring IoC, and Hibernate.",
      "Practiced Agile methodology, requirement analysis, and production defect triage.",
      "Wrote JUnit tests, implemented structured logging, and participated in CI/CD using Jenkins.",
    ],
  },
];

const EDUCATION = [
  // Fill in actual education when ready
  {
    school: "Your University Name",
    degree: "B.S. / M.S. in Computer Science (placeholder)",
    year: "20XX",
    location: "City, Country",
  },
];

const CERTIFICATIONS = [
  // Optional: list of relevant certs
  "AWS or Azure Certification (placeholder)",
];

function Pill({ children }) {
  return (
    <span className="badge bg-light text-muted border me-1 mb-1">{children}</span>
  );
}

function CopyButton({ text, label = "Copy" }) {
  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          alert(`${label} copied: ${text}`);
        } catch {}
      }}
    >
      📋 {label}
    </button>
  );
}

export default function KhusbuThapaProfile() {
  // Title + JSON-LD for SEO
  useEffect(() => {
    document.title = "Khusbu Thapa — Profile";
  }, []);

  const personJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Khusbu Thapa",
      jobTitle: "Agile Developer II",
      worksFor: { "@type": "Organization", name: "Freddie Mac" },
      url: LINKS.linkedin,
      sameAs: [LINKS.linkedin].concat(LINKS.github ? [LINKS.github] : []),
      email: `mailto:${EMAIL}`,
      telephone: PHONE,
      address: {
        "@type": "PostalAddress",
        addressLocality: "Tysons Corner / Reston",
        addressRegion: "VA",
        addressCountry: "US",
      },
      knowsAbout: SKILL_GROUPS.flatMap((g) => g.items),
    }),
    []
  );

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: Profile summary + On this page */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card shadow-sm border-0 text-center mb-3">
              <div className="card-body">
                <img
                  src={avatar}
                  alt="Khusbu Thapa"
                  className="rounded-circle mb-2"
                  width="96"
                  height="96"
                />
                <h1 className="h6 mb-1">Khusbu Thapa</h1>
                <p className="small text-muted mb-2">{TAGLINE}</p>
                <div className="d-grid gap-2">
                  <a
                    className="btn btn-primary btn-sm"
                    href={LINKS.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn
                  </a>
                  {LINKS.github && (
                    <a
                      className="btn btn-outline-dark btn-sm"
                      href={LINKS.github}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      GitHub
                    </a>
                  )}
                  {LINKS.resume && (
                    <a
                      className="btn btn-outline-secondary btn-sm"
                      href={LINKS.resume}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download Resume
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h6 text-uppercase text-muted mb-2">
                  On this page
                </h2>
                <nav className="nav flex-column small">
                  <a className="nav-link px-0" href="#about">About</a>
                  <a className="nav-link px-0" href="#skills">Skills</a>
                  <a className="nav-link px-0" href="#experience">Experience</a>
                  <a className="nav-link px-0" href="#education">Education</a>
                  <a className="nav-link px-0" href="#certs">Certifications</a>
                  <a className="nav-link px-0" href="#contact">Contact</a>
                </nav>
              </div>
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          {/* Hero */}
          <header className="mb-3">
            <div className="d-flex align-items-center gap-3">
              <img
                src={avatar}
                alt="Khusbu Thapa"
                className="rounded-circle d-xl-none"
                width="64"
                height="64"
              />
              <div>
                <h1 className="h3 mb-1">Khusbu Thapa</h1>
                <div className="text-muted">{TAGLINE}</div>
              </div>
            </div>
          </header>

          {/* About */}
          <section id="about" className="mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h5 mb-2">About</h2>
                <p className="mb-0">{ABOUT}</p>
              </div>
            </div>
          </section>

          {/* Quick facts */}
          <section className="mb-4">
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="text-muted small">Location</div>
                    <div className="fw-semibold">{LOCATION}</div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="text-muted small">Email</div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                      <CopyButton text={EMAIL} label="Email" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="text-muted small">Phone</div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <a href={`tel:${PHONE.replace(/[^0-9]/g, "")}`}>{PHONE}</a>
                      <CopyButton text={PHONE} label="Phone" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Skills */}
          <section id="skills" className="mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h5 mb-3">Skills</h2>
                <div className="row g-3">
                  {SKILL_GROUPS.map((g) => (
                    <div className="col-12 col-md-6" key={g.label}>
                      <div className="mb-1 fw-semibold">{g.label}</div>
                      <div>
                        {g.items.map((i) => (
                          <Pill key={i}>{i}</Pill>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Experience */}
          <section id="experience" className="mb-4">
            <h2 className="h5 mb-3">Experience</h2>
            {EXPERIENCE.map((job) => (
              <div className="card shadow-sm border-0 mb-3" key={`${job.company}-${job.role}-${job.period}`}>
                <div className="card-body">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div>
                      <div className="fw-semibold">
                        {job.role} — {job.company}
                      </div>
                      <div className="text-muted small">
                        {job.location} • {job.period}
                      </div>
                    </div>
                  </div>
                  <ul className="mt-2 mb-0">
                    {job.bullets.map((b, i) => (
                      <li key={i} className="mb-1">{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </section>

          {/* Education */}
          <section id="education" className="mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h5 mb-2">Education</h2>
                <ul className="mb-0">
                  {EDUCATION.map((e) => (
                    <li key={e.school} className="mb-1">
                      <span className="fw-semibold">{e.degree}</span>, {e.school} ({e.year}) — {e.location}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Certifications */}
          <section id="certs" className="mb-5">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h2 className="h5 mb-2">Certifications</h2>
                <ul className="mb-0">
                  {CERTIFICATIONS.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Contact / CTA */}
          <section id="contact" className="mb-5">
            <div className="card shadow-sm border-0">
              <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <h2 className="h5 mb-1">Let’s connect</h2>
                  <div className="text-muted">
                    Open to full-time roles in enterprise Java/Angular and cloud-native development.
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <a className="btn btn-primary" href={LINKS.linkedin} target="_blank" rel="noopener noreferrer">
                    Message on LinkedIn
                  </a>
                  <a className="btn btn-outline-secondary" href={`mailto:${EMAIL}`}>
                    Email
                  </a>
                  <Link className="btn btn-outline-dark" to="/projects">
                    See Projects
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* JSON-LD for SEO */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(personJsonLd) }}
          />
        </main>

        {/* RIGHT: simple featured links / notes */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h2 className="h6 text-uppercase text-muted mb-2">Featured Links</h2>
              <ul className="list-unstyled mb-0">
                <li className="mb-1">
                  <a href={LINKS.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn Profile ↗</a>
                </li>
                {LINKS.github && (
                  <li className="mb-1">
                    <a href={LINKS.github} target="_blank" rel="noopener noreferrer">GitHub Profile ↗</a>
                  </li>
                )}
                {LINKS.resume && (
                  <li className="mb-1">
                    <a href={LINKS.resume} target="_blank" rel="noopener noreferrer">Download Resume (PDF) ↗</a>
                  </li>
                )}
              </ul>
              <hr />
              <div className="small text-muted">
                Avatar and any portfolio screenshots are placeholders—swap with real images anytime.
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
